/**
 * Licensed Materials - Property of IBM
 *
 * "Restricted Materials of IBM"
 *
 * (C) Copyright IBM Corp. 2019, 2020
 *
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import React, { Component } from 'react';
import { observable } from 'mobx';
import { observer } from 'mobx-react';
import Measure from 'react-measure';

import PropTypes from 'util/PropTypes';
import classNames from 'classnames';

import { RangeSlider, SVGIcon, Label } from 'ca-ui-toolkit';
import StringResources from '../../../nls/StringResources';
import './relationshipVisView.scss';
import RelationshipInfoTooltip from './RelationshipInfoTooltip';
import getInformation16 from '@ba-ui-toolkit/ba-graphics/dist/icons/get-information_16.svg';

const SIZES = {
	'small': [0, 699],
	'large': [700, Infinity]
};

@observer
class RelationshipStrength  extends Component {
	@observable sliderRange = null;

	/***
	 *@constructor
	 **/
	constructor(props) {
		super(props);
		this.sliderRange = {
			min: this.props.targetModel.computedMinStrength,
			max: this.props.targetModel.computedMaxStrength
		};
		this.state = {
			size: undefined,
			isInfoTooltipOpen: false
		};
		this.strengthInfoRef = React.createRef();
	}

	componentWillReceiveProps(newProps) {
		// Update slider state when we have new relationship min-max strength passed in props
		this.sliderRange = {
			min: newProps.targetModel.computedMinStrength,
			max: newProps.targetModel.computedMaxStrength
		};
		this.setState(this.state);
	}

	/* Render the relationship toolbar which includes filter strength slider and checkbox for view type */
	render() {
		const sliderLabel = StringResources.get('relationshipGraph_filterStrength_label');
		const { size } = this.state;
		const classes = classNames({
			'relationship-vis-view__strength': true,
			'small': size === 'small',
			'large': size === 'large'
		});
		return (
			<Measure
				onResize={(contentRect) => this._onResize(contentRect)}
			>
				{({ measureRef }) =>
					<div className={classes} ref={measureRef}>
						{this._buildInformationMarkup(sliderLabel)}
						{this._buildSliderMarkup()}
					</div>
				}
			</Measure>
		);
	}

	_onResize({ entry: { width } = {} } = {}) {
		if (width) {
			const size = Object.keys(SIZES).find((key) => {
				const [ min, max ] = SIZES[key];
				return width >= min && width <= max;
			});

			if (size) {
				this.setState({
					size
				});
			}
		}
	}

	_buildInformationMarkup(sliderLabel) {
		return (
			<div className="relationship-vis-view__strength__infoParent">
				<div
					ref={this.strengthInfoRef}
					className="relationship-vis-view__strength__info"
					tabIndex={0}
					aria-label={sliderLabel}
					onClick={this._onInfoClick}
					onKeyDown={this._onKeyDown}
				>
					<SVGIcon iconId={getInformation16.id} intent={'primary'} />
					<div className="relationship-vis-view__strength__infoTooltip">
						<RelationshipInfoTooltip
							isOpen = {this.state.isInfoTooltipOpen}
							triggerNode = {this.strengthInfoRef.current}
							onClose = {this.resetFlyout}
						/>
					</div>
				</div>
				<Label className="relationship-vis-view__strength__infoLabel" label={sliderLabel}/>
			</div>
		);
	}

	resetFlyout = () => {
		this.setState({
			isInfoTooltipOpen: false
		});
		// reset focus on the Info icon
		if (this.strengthInfoRef.current) {
			this.strengthInfoRef.current.focus();
		}
	}

	_onKeyDown = (event) => {
		switch (event.key) {
			case ' ': // space
			case 'Enter':
				this._onInfoClick(event);
		}
	}

	_onInfoClick = (event) => {
		// avoid reacting to the click on X icon when the info flyout is opened
		if (event && event.target && event.target.classList && event.target.classList.contains('relationship-vis-view__strength__info')) {
			// toggle InfoTooltip
			this.setState({
				isInfoTooltipOpen: !this.state.isInfoTooltipOpen
			});
		}
	}

	_buildSliderMarkup() {
		const { size } = this.state;
		const classes = classNames({
			'relationship-vis-view__strength__slider': true,
			'small': size === 'small',
			'large': size === 'large'
		});
		const toPercent = (number) => {
			if(!number) {
				return null;
			}
			return number.toString() + '%';
		};
		return (
			<div className={classes}>
				<RangeSlider
					showTooltip
					showBoundaryLabels
					className = "relationship-vis-view__strength__slider__range"
					values={[this.sliderRange.min, this.sliderRange.max]}
					labelFormatter={toPercent}
					onChange={values => this._onValueChange(...values)}
					onInputComplete={() => this._onInputComplete()}
					size='small'
					min={10} max={100} />
			</div>
		);
	}

	_onValueChange(min, max) {
		this.sliderRange = { min, max };
	}

	_onInputComplete() {
		this.props.targetModel.setFilterStrength({ min: this.sliderRange.min, max: this.sliderRange.max });
	}

}

RelationshipStrength.propTypes = {
	targetModel: PropTypes.object.isRequired
};


export default RelationshipStrength ;



// WEBPACK FOOTER //
// ./src/js/components/startingPoints/relationshipGraph/RelationshipStrength.js